import { tabUnderlineClasses, tabUnderlineColor, tabUnderlineThickness } from "@ui/components/internal/TabUnderline";
import { tv } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  Tab,
  TabList,
  type TabListProps,
  TabPanel,
  type TabPanelProps,
  type TabProps,
  Tabs,
  type TabsProps,
} from "@ui/primitives/Tabs";
import { createContext, useContext } from "react";

type SectionTabSize = "sm" | "responsive" | "header";

const SectionTabSizeContext = createContext<SectionTabSize>("responsive");

/**
 * `sectionTabList`/`sectionTab` are exported for tab-shaped navs where RAC
 * `Tabs` client state is not the source of truth:
 *
 * - URL-routed link-tabs: a `<nav>` of links, the URL decides the active tab.
 *   Set `data-selected` on the active link (it drives the `selected:`
 *   variants) plus `aria-current="page"`.
 * - Scrollspy quick-jump (`SectionJumpNav`): a `<nav>` of buttons, the scroll
 *   position decides. Same `data-selected` contract, `aria-current="location"`.
 */
export const sectionTabList = tv({
  base: ["flex overflow-x-auto overflow-y-hidden", "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"],
  defaultVariants: { size: "responsive" },
  variants: {
    size: {
      sm: "px-5 gap-9 shadow-[inset_0_-1px_0_0_var(--color-neutral-200)]",
      responsive: "px-5 gap-9 lg:gap-4 lg:px-0 shadow-[inset_0_-1px_0_0_var(--color-neutral-200)]",
      header: "gap-6",
    },
  },
});

const compactTabStyles = [
  "flex-none pb-2 text-base font-semibold text-neutral-400",
  "selected:text-neutral-900",
] as const;

/** The tab half of the URL-routed link-tabs pattern — see {@link sectionTabList}. */
export const sectionTab = tv({
  base: [
    "relative cursor-pointer outline-none whitespace-nowrap",
    tabUnderlineClasses,
    tabUnderlineThickness["0.5"],
    tabUnderlineColor["green-500"],
    "selected:after:opacity-100",
  ],
  defaultVariants: { size: "responsive" },
  variants: {
    size: {
      sm: [...compactTabStyles],
      responsive: [...compactTabStyles, "lg:flex-1 lg:text-display-xs lg:font-extrabold lg:after:h-1"],
      header: [
        ...compactTabStyles,
        "md:pb-4 md:text-2xl md:font-extrabold md:tracking-2xl md:text-neutral-300",
        "md:selected:text-neutral-900",
        "md:after:-bottom-px",
      ],
    },
  },
});

const sectionTabPanel = tv({
  base: "outline-none",
});

export interface SectionTabsProps extends TabsProps {
  /**
   * - `"sm"` — always compact styling.
   * - `"responsive"` (default) — compact on mobile, large at `lg` breakpoint.
   * - `"header"` — compact on mobile, header-title-sized on desktop. Designed for
   *   modals where tabs replace the modal title.
   */
  size?: SectionTabSize;
}

export function SectionTabs({ size = "responsive", ...props }: SectionTabsProps) {
  return (
    <SectionTabSizeContext.Provider value={size}>
      <Tabs {...props} />
    </SectionTabSizeContext.Provider>
  );
}

export interface SectionTabListProps<T extends object> extends TabListProps<T> {}

export function SectionTabList<T extends object>(props: SectionTabListProps<T>) {
  const size = useContext(SectionTabSizeContext);
  return (
    <TabList
      {...props}
      className={composeRenderProps(props.className, (className) => sectionTabList({ size, className }))}
    />
  );
}

export interface SectionTabProps extends TabProps {}

export function SectionTab(props: SectionTabProps) {
  const size = useContext(SectionTabSizeContext);
  return (
    <Tab
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        sectionTab({ ...renderProps, size, className }),
      )}
    />
  );
}

export interface SectionTabPanelProps extends TabPanelProps {}

export function SectionTabPanel(props: SectionTabPanelProps) {
  return (
    <TabPanel
      {...props}
      className={composeRenderProps(props.className, (className) => sectionTabPanel({ className }))}
    />
  );
}
